import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis';
import type { OptionsMap } from '@openx/types/options';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface FiltersDropdownProps {
  options: OptionsMap;
  loading: boolean;
  selectedOptions: readonly string[];
  placeholder: string;
  onChange: (values: string[]) => void;
  fetchOptions?: (options) => void;
  debounced?: boolean;
  dataTest?: string;
}

const StyledOptionDiv = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(0.8, 1.5)};
  width: 100%;
`;

export function FiltersDropdown(props: FiltersDropdownProps) {
  const { options, loading, selectedOptions, placeholder, onChange, dataTest, fetchOptions, debounced } = props;
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!fetchOptions) return;
    if (search.length < 2) return;
    fetchOptions(search);
  }, [fetchOptions, search]);

  const handleChange = useCallback(
    (e, value: string) => {
      const next = selectedOptions.includes(value)
        ? selectedOptions.filter(id => id !== value)
        : [...selectedOptions, value];
      onChange(next);
      e.stopPropagation();
    },
    [onChange, selectedOptions]
  );

  const optionsIds = useMemo(() => produceSortedOptionsKeys(options), [options]);

  const filteredOptions = useMemo(() => {
    const s = search.toLowerCase();
    return s.length > 2 ? optionsIds.filter(option => options[option]?.name.toLowerCase().includes(s)) : optionsIds;
  }, [options, optionsIds, search]);

  const renderOption = useCallback(
    (state, option) => {
      const Icon = selectedOptions.includes(option) ? RemoveIcon : AddIcon;
      return (
        <li {...state} key={option}>
          <StyledOptionDiv onClick={e => handleChange(e, option)}>
            <Ellipsis width="85%" tooltip>
              {options[option]?.name || option}
            </Ellipsis>
            <IconButton size="small">
              <Icon />
            </IconButton>
          </StyledOptionDiv>
        </li>
      );
    },
    [handleChange, options, selectedOptions]
  );

  return (
    <AutoCompleteVirtualize
      textFieldProps={{ margin: 'dense', placeholder: placeholder }}
      disableClearable
      renderOption={renderOption}
      isOptionEqualToValue={option => selectedOptions.includes(option)}
      getOptionLabel={option => options[option]?.name || option}
      onDebounceChange={e => setSearch(e.target.value)}
      onClose={() => setSearch('')}
      options={filteredOptions}
      loading={loading}
      value={search}
      freeSolo
      debounced={!!debounced}
      data-test={dataTest}
    />
  );
}
